/**
 * Admin Hub Configuration
 * Defines navigation tree, permissions, and layout settings
 */
import React from 'react';
import {
  AdminPanelSettings as SystemIcon,
  Store as MarketCompassIcon,
  AccountBalance as AccountingIcon,
  Description as DeliverablesIcon,
  Support as SupportIcon,
  People as UsersIcon,
  Security as PermissionsIcon,
  Assessment as MonitoringIcon,
  SmartToy as AIIcon,
  Webhook as WebhookIcon,
  TrendingUp as RankingsIcon,
  Tune as CustomizationIcon,
  Settings as OperationsIcon,
  Business as VendorsIcon,
  Receipt as InvoicesIcon,
  ReceiptLong as BillsIcon,
  CompareArrows as MappingIcon,
  Folder as QuickBooksIcon,
  History as AuditIcon,
  Article as DocumentsIcon,
  AutoAwesome as PromptsIcon,
  Science as SandboxIcon,
  Map as MarketsIcon,
  Person as MyIcon,
  ListAlt as TemplatesIcon,
  Handshake as PartnershipIcon,
  Terminal as LogsIcon,
  Schedule as ScheduleIcon,
  Workspaces as GroupingsIcon,
  Payment as StripeIcon,
  Insights as AnalyticsIcon,
  CloudUpload as DataLoadIcon,
  Summarize as ReportsIcon,
  MenuBook as InstructionsIcon,
  TableChart as TablesIcon,
  AutoFixHigh as WizardsIcon,
  FactCheck as ReconciliationIcon,
} from '@mui/icons-material';

// Permission categories
export const FULL_ADMIN_PERMISSIONS = [
  'permissions.all',
  'market_compass.admin',
  'accounting.admin',
  'accounting.all',
  'deliverables.all',
  'messages.all',
  'developer_tools.view',
] as const;

export const CONFIG_ONLY_PERMISSIONS = [
  'accounting.view',
  'market_compass.customize',
] as const;

// Admin Hub permission constants
export const ADMIN_PERMISSIONS = {
  SYSTEM_ADMIN: 'permissions.all',
  MARKET_COMPASS_ADMIN: 'market_compass.admin',
  MARKET_COMPASS_CUSTOMIZE: 'market_compass.customize',
  ACCOUNTING_VIEW: 'accounting.view',
  ACCOUNTING_ADMIN: 'accounting.admin',
  ACCOUNTING_ALL: 'accounting.all',
  ACCOUNTING_GPHTL: 'accounting.gphtl',
  DELIVERABLES_ALL: 'deliverables.all',
  MESSAGES_ALL: 'messages.all',
  DEVELOPER_TOOLS_VIEW: 'developer_tools.view',
} as const;

// Layout mode type
export type AdminLayoutMode = 'full' | 'config-only' | 'none';

/**
 * Determine the layout mode based on user permissions
 */
export const getAdminLayoutMode = (permissions: string[]): AdminLayoutMode => {
  // Check for full admin permissions first
  const hasFullAdmin = FULL_ADMIN_PERMISSIONS.some(p => permissions.includes(p));
  if (hasFullAdmin) return 'full';

  // Check for config-only permissions
  const hasConfigOnly = CONFIG_ONLY_PERMISSIONS.some(p => permissions.includes(p));
  if (hasConfigOnly) return 'config-only';

  return 'none';
};

/**
 * Check if user has any admin access
 */
export const hasAnyAdminAccess = (permissions: string[]): boolean => {
  return getAdminLayoutMode(permissions) !== 'none';
};

// Navigation tree item interface
export interface TreeNavItem {
  id: string;
  label: string;
  /** 'subheader' renders as a non-interactive group label; its children render inline at the same level */
  type?: 'subheader';
  icon?: React.ReactNode;
  path?: string;
  permission?: string;
  permissions?: string[];
  /**
   * Permissions required to navigate to `path` (OR logic), when stricter than the
   * visibility permission(s). If the user lacks all of these, the node stays visible
   * but its path is stripped (e.g. a section header that only toggles expansion).
   */
  pathPermissions?: string[];
  badge?: {
    count: number;
    color: 'error' | 'warning' | 'info';
  };
  children?: TreeNavItem[];
  defaultExpanded?: boolean;
}

// Build the navigation tree configuration
// Two levels deep: section -> page. Subheader nodes provide visual grouping within a
// section without adding click/indent depth. Pages whose sub-views are tabs (Permissions,
// GP HTL Reports) get a single entry; the page's own tabs handle sub-navigation.
export const buildNavigationTree = (): TreeNavItem[] => [
  {
    id: 'system',
    label: 'System',
    icon: React.createElement(SystemIcon),
    permissions: [ADMIN_PERMISSIONS.SYSTEM_ADMIN, ADMIN_PERMISSIONS.DEVELOPER_TOOLS_VIEW],
    path: '/admin/hub/system',
    pathPermissions: [ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN],
    defaultExpanded: false,
    children: [
      { id: 'system-permissions', label: 'Permissions', icon: React.createElement(PermissionsIcon), path: '/admin/hub/system/permissions', permission: ADMIN_PERMISSIONS.SYSTEM_ADMIN },
      {
        id: 'system-subheader-ai',
        label: 'AI & LLM',
        type: 'subheader',
        children: [
          { id: 'system-mia-oversight', label: 'Mia Oversight', icon: React.createElement(AIIcon), path: '/admin/hub/system/mia', permission: ADMIN_PERMISSIONS.SYSTEM_ADMIN },
          { id: 'system-llm-management', label: 'Providers & Models', icon: React.createElement(CustomizationIcon), path: '/admin/hub/system/llm', permission: ADMIN_PERMISSIONS.SYSTEM_ADMIN },
          { id: 'system-prompts', label: 'Prompts', icon: React.createElement(PromptsIcon), path: '/admin/hub/system/prompts', permission: ADMIN_PERMISSIONS.SYSTEM_ADMIN },
          { id: 'system-llm-activity', label: 'Activity Log', icon: React.createElement(MonitoringIcon), path: '/admin/hub/system/llm-activity', permissions: [ADMIN_PERMISSIONS.SYSTEM_ADMIN, ADMIN_PERMISSIONS.DEVELOPER_TOOLS_VIEW] },
        ],
      },
      {
        id: 'system-subheader-devtools',
        label: 'Developer Tools',
        type: 'subheader',
        children: [
          { id: 'system-logs', label: 'Logs', icon: React.createElement(LogsIcon), path: '/admin/hub/system/logs', permissions: [ADMIN_PERMISSIONS.SYSTEM_ADMIN, ADMIN_PERMISSIONS.DEVELOPER_TOOLS_VIEW] },
          { id: 'system-scheduled-tasks', label: 'Scheduled Tasks', icon: React.createElement(ScheduleIcon), path: '/admin/hub/system/scheduled-tasks', permissions: [ADMIN_PERMISSIONS.SYSTEM_ADMIN, ADMIN_PERMISSIONS.DEVELOPER_TOOLS_VIEW] },
          { id: 'system-hubspot-logs', label: 'HubSpot Webhooks', icon: React.createElement(WebhookIcon), path: '/admin/hub/system/webhooks/hubspot', permission: ADMIN_PERMISSIONS.SYSTEM_ADMIN },
          { id: 'system-quickbooks-logs', label: 'QuickBooks Webhooks', icon: React.createElement(WebhookIcon), path: '/admin/hub/system/webhooks/quickbooks', permission: ADMIN_PERMISSIONS.SYSTEM_ADMIN },
        ],
      },
    ],
  },
  {
    id: 'marketcompass',
    label: 'MarketCompass',
    icon: React.createElement(MarketCompassIcon),
    permissions: [ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN, ADMIN_PERMISSIONS.MARKET_COMPASS_CUSTOMIZE],
    path: '/admin/hub/marketcompass',
    pathPermissions: [ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN],
    defaultExpanded: false,
    children: [
      { id: 'marketcompass-my-markets', label: 'My Markets', icon: React.createElement(MyIcon), path: '/admin/hub/marketcompass/my-markets', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_CUSTOMIZE },
      { id: 'marketcompass-market-management', label: 'Markets', icon: React.createElement(MarketsIcon), path: '/admin/hub/marketcompass/markets', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
      {
        id: 'marketcompass-subheader-rankings',
        label: 'Rankings & Groupings',
        type: 'subheader',
        children: [
          { id: 'marketcompass-market-rankings', label: 'Rankings', icon: React.createElement(RankingsIcon), path: '/admin/hub/marketcompass/rankings', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
          { id: 'marketcompass-weighting-profiles', label: 'Weighting Profiles', icon: React.createElement(CustomizationIcon), path: '/admin/hub/marketcompass/rankings/weighting-profiles', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
          { id: 'marketcompass-rank-groupings', label: 'Rank Groupings', icon: React.createElement(GroupingsIcon), path: '/admin/hub/marketcompass/groupings', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
          { id: 'marketcompass-customization-groupings', label: 'Customization Groupings', icon: React.createElement(GroupingsIcon), path: '/admin/hub/marketcompass/customization', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
        ],
      },
      {
        id: 'marketcompass-subheader-reports',
        label: 'Reports',
        type: 'subheader',
        children: [
          { id: 'marketcompass-custom-reports', label: 'Comp Set Reports', icon: React.createElement(ReportsIcon), path: '/admin/hub/marketcompass/reports', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
          { id: 'marketcompass-report-instructions', label: 'Report Instructions', icon: React.createElement(InstructionsIcon), path: '/admin/hub/marketcompass/report-instructions', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
        ],
      },
      {
        id: 'marketcompass-subheader-operations',
        label: 'Operations',
        type: 'subheader',
        children: [
          { id: 'marketcompass-stripe', label: 'Stripe', icon: React.createElement(StripeIcon), path: '/admin/hub/marketcompass/stripe', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
          { id: 'marketcompass-analytics', label: 'Analytics', icon: React.createElement(AnalyticsIcon), path: '/admin/hub/marketcompass/analytics', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
          { id: 'marketcompass-data-load', label: 'Data Load Instructions', icon: React.createElement(DataLoadIcon), path: '/admin/hub/marketcompass/data-load', permission: ADMIN_PERMISSIONS.MARKET_COMPASS_ADMIN },
        ],
      },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    icon: React.createElement(AccountingIcon),
    permissions: [ADMIN_PERMISSIONS.ACCOUNTING_ADMIN, ADMIN_PERMISSIONS.ACCOUNTING_ALL, ADMIN_PERMISSIONS.ACCOUNTING_VIEW, ADMIN_PERMISSIONS.ACCOUNTING_GPHTL],
    path: '/admin/hub/accounting',
    defaultExpanded: false,
    children: [
      { id: 'accounting-my-vendors', label: 'My Vendors', icon: React.createElement(MyIcon), path: '/admin/hub/accounting/my-vendors', permission: ADMIN_PERMISSIONS.ACCOUNTING_VIEW },
      {
        id: 'accounting-subheader-records',
        label: 'Records',
        type: 'subheader',
        children: [
          { id: 'accounting-vendors', label: 'Vendors', icon: React.createElement(VendorsIcon), path: '/admin/hub/accounting/vendors', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-users', label: 'Accounting Users', icon: React.createElement(UsersIcon), path: '/admin/hub/accounting/users', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-invoices', label: 'Invoices', icon: React.createElement(InvoicesIcon), path: '/admin/hub/accounting/invoices', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-bills', label: 'Bills', icon: React.createElement(BillsIcon), path: '/admin/hub/accounting/bills', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-mapping', label: 'Invoice/Bill Mapping', icon: React.createElement(MappingIcon), path: '/admin/hub/accounting/mapping', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-reconciliation', label: 'Reconciliation', icon: React.createElement(ReconciliationIcon), path: '/admin/hub/accounting/reconciliation', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-reconciliation-settings', label: 'Reconciliation Settings', icon: React.createElement(OperationsIcon), path: '/admin/hub/accounting/reconciliation/settings', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
        ],
      },
      {
        id: 'accounting-subheader-integrations',
        label: 'Integrations & Reporting',
        type: 'subheader',
        children: [
          { id: 'accounting-quickbooks-settings', label: 'QuickBooks Settings', icon: React.createElement(QuickBooksIcon), path: '/admin/hub/accounting/quickbooks', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-quickbooks-tables', label: 'QuickBooks Tables', icon: React.createElement(TablesIcon), path: '/admin/hub/accounting/quickbooks/tables', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
          { id: 'accounting-gphtl', label: 'GP HTL Reports', icon: React.createElement(PartnershipIcon), path: '/admin/hub/accounting/gphtl', permissions: [ADMIN_PERMISSIONS.ACCOUNTING_ADMIN, ADMIN_PERMISSIONS.ACCOUNTING_GPHTL] },
          { id: 'accounting-audit', label: 'Audit Logs', icon: React.createElement(AuditIcon), path: '/admin/hub/accounting/audit', permission: ADMIN_PERMISSIONS.ACCOUNTING_ADMIN },
        ],
      },
    ],
  },
  {
    id: 'deliverables',
    label: 'Deliverables',
    icon: React.createElement(DeliverablesIcon),
    permission: ADMIN_PERMISSIONS.DELIVERABLES_ALL,
    path: '/admin/hub/deliverables',
    defaultExpanded: false,
    children: [
      { id: 'deliverables-doc-id', label: 'Documents', icon: React.createElement(DocumentsIcon), path: '/admin/hub/deliverables/documents', permission: ADMIN_PERMISSIONS.DELIVERABLES_ALL },
      { id: 'deliverables-wizards', label: 'Wizards', icon: React.createElement(WizardsIcon), path: '/admin/hub/deliverables/wizards', permission: ADMIN_PERMISSIONS.DELIVERABLES_ALL },
      { id: 'deliverables-templates-list', label: 'Templates', icon: React.createElement(TemplatesIcon), path: '/admin/hub/deliverables/templates', permission: ADMIN_PERMISSIONS.DELIVERABLES_ALL },
      { id: 'deliverables-prompts', label: 'Blurb Prompts', icon: React.createElement(PromptsIcon), path: '/admin/hub/deliverables/prompts', permission: ADMIN_PERMISSIONS.DELIVERABLES_ALL },
      { id: 'deliverables-sandbox', label: 'Sandbox', icon: React.createElement(SandboxIcon), path: '/admin/hub/deliverables/sandbox', permission: ADMIN_PERMISSIONS.DELIVERABLES_ALL },
    ],
  },
  {
    id: 'support',
    label: 'Support Tickets',
    icon: React.createElement(SupportIcon),
    path: '/admin/hub/support',
    permission: ADMIN_PERMISSIONS.MESSAGES_ALL,
  },
];

/**
 * Filter navigation tree based on user permissions
 */
export const filterByPermission = (
  nodes: TreeNavItem[],
  userPermissions: string[]
): TreeNavItem[] => {
  return nodes
    .filter(node => {
      // No permission required
      if (!node.permission && !node.permissions) return true;

      // Single permission check
      if (node.permission) {
        return userPermissions.includes(node.permission);
      }

      // Multiple permissions (OR logic)
      if (node.permissions) {
        return node.permissions.some(p => userPermissions.includes(p));
      }

      return false;
    })
    .map(node => {
      // Strip the path when navigation requires stricter permissions than visibility
      // (e.g. a section header whose landing page the user cannot access)
      const canNavigate = !node.pathPermissions
        || node.pathPermissions.some(p => userPermissions.includes(p));
      return {
        ...node,
        path: canNavigate ? node.path : undefined,
        children: node.children
          ? filterByPermission(node.children, userPermissions)
          : undefined
      };
    })
    .filter(node => {
      // Remove parent nodes with no visible children (unless they have a path)
      if (node.children && node.children.length === 0 && !node.path) {
        return false;
      }
      return true;
    });
};

/**
 * LocalStorage keys for Admin Hub state
 */
export const ADMIN_HUB_STORAGE_KEYS = {
  SIDEBAR_COLLAPSED: 'admin-hub-sidebar-collapsed',
  EXPANDED_NODES: 'admin-hub-expanded-nodes',
  FAVORITES: 'admin-hub-favorites',
  RECENTS: 'admin-hub-recents',
  QUICK_ACCESS_COLLAPSED: 'admin-hub-quick-access-collapsed',
} as const;

/**
 * Admin Hub visual design constants
 */
export const ADMIN_HUB_DESIGN = {
  SIDEBAR_WIDTH_EXPANDED: 260,
  SIDEBAR_WIDTH_COLLAPSED: 56,
  TREE_ITEM_HEIGHT: 36,
  TREE_INDENT: 16,
  ANIMATION_DURATION: 200,
  COLORS: {
    SIDEBAR_BG: '#f5f5f5',
    ACTIVE_ITEM: '#F5A800',
    ACTIVE_ITEM_BG: 'rgba(245, 168, 0, 0.1)',
    HOVER_BG: 'rgba(0, 0, 0, 0.04)',
    SECTION_HEADER: '#666666',
    TREE_LINES: '#e0e0e0',
  },
} as const;

/**
 * Get flattened list of all navigable pages for search
 */
export const getAllNavigablePages = (nodes: TreeNavItem[]): Array<{ id: string; label: string; path: string; section: string }> => {
  const pages: Array<{ id: string; label: string; path: string; section: string }> = [];

  const traverse = (items: TreeNavItem[], section: string = '') => {
    for (const item of items) {
      const currentSection = section || item.label;
      if (item.path) {
        pages.push({
          id: item.id,
          label: item.label,
          path: item.path,
          section: currentSection,
        });
      }
      if (item.children) {
        traverse(item.children, currentSection);
      }
    }
  };

  traverse(nodes);
  return pages;
};

/**
 * Filter navigation tree based on search text
 * Returns only nodes that match or have matching descendants
 */
export const filterNavigationTree = (
  nodes: TreeNavItem[],
  searchText: string
): TreeNavItem[] => {
  const lowerSearch = searchText.toLowerCase().trim();
  if (!lowerSearch) return nodes;

  const filterNodes = (items: TreeNavItem[]): TreeNavItem[] => {
    const results: TreeNavItem[] = [];

    for (const node of items) {
      // A matching container (section/subheader) keeps its whole subtree
      const labelMatches = node.label.toLowerCase().includes(lowerSearch);
      if (labelMatches) {
        results.push(node);
        continue;
      }

      // Otherwise include only when it has matching descendants
      const filteredChildren = node.children
        ? filterNodes(node.children)
        : undefined;

      if (filteredChildren && filteredChildren.length > 0) {
        results.push({
          ...node,
          children: filteredChildren,
        });
      }
    }

    return results;
  };

  return filterNodes(nodes);
};

/**
 * Find the path of node IDs from root to a node with the given path
 * Returns array of node IDs that need to be expanded to show the target
 */
export const findPathToNode = (
  nodes: TreeNavItem[],
  targetPath: string
): string[] => {
  const pathIds: string[] = [];

  const findPath = (items: TreeNavItem[], currentPath: string[] = []): boolean => {
    for (const item of items) {
      // Check if this item's path matches (handle query strings)
      const itemBasePath = item.path?.split('?')[0];
      const targetBasePath = targetPath.split('?')[0];

      if (item.path === targetPath || itemBasePath === targetBasePath) {
        // Found it! Return the path to get here
        pathIds.push(...currentPath);
        return true;
      }

      if (item.children) {
        // Try to find in children
        if (findPath(item.children, [...currentPath, item.id])) {
          return true;
        }
      }
    }
    return false;
  };

  findPath(nodes);
  return pathIds;
};
